import type { MosaicBranch } from 'react-mosaic-component'
import type { ComponentType } from '@/types/schemas/workspace'
import type { DropPosition } from './resolve-drop'

/**
 * Fixed id handed to react-mosaic so the Panel Hub's cards can drag INTO it.
 * react-mosaic's drop targets only answer a drag whose item carries the same
 * `mosaicId`; without a fixed one it mints a uuid per mount that nothing
 * outside the mosaic could know.
 */
export const WORKSPACE_MOSAIC_ID = 'workspace-mosaic'

/** The sheet's width. Two columns of cards at ~170px each plus gutters. */
export const PANEL_HUB_WIDTH_PX = 380

/**
 * What a hub card puts on the react-dnd wire. `mosaicId` and `hideTimer` are
 * the shape react-mosaic expects of its own window drags (`MosaicDragItem`);
 * `componentType` is ours and is what tells the drag layer and the drop
 * handler that this drag came from the hub, not from an existing tile.
 */
export interface PanelHubDragItem {
  mosaicId: string
  hideTimer: number
  componentType: ComponentType
}

/** What react-mosaic's drop targets hand back to the source's `end` handler. */
export interface PanelHubDropResult {
  path?: MosaicBranch[]
  position?: DropPosition
}

export function isPanelHubDragItem(item: unknown): item is PanelHubDragItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    typeof (item as PanelHubDragItem).componentType === 'string'
  )
}

/**
 * What a card's react-dnd `end` handler does once the drag is over, kept
 * pure so the landed/not-landed branching is unit-testable without a drag
 * backend. Returns whether a panel was inserted.
 */
export function finishHubDrag(
  item: PanelHubDragItem,
  dropResult: PanelHubDropResult | null | undefined,
  onDrop: (componentType: ComponentType, path: MosaicBranch[], position: DropPosition) => boolean,
): boolean {
  if (dropResult?.path == null || dropResult.position == null) return false
  return onDrop(item.componentType, dropResult.path, dropResult.position)
}
